import React from "react";
import { Link } from "react-router-dom";
import {
  ArrowLeft,
  Phone,
  Mail,
  Globe,
  Compass,
  Sparkles,
  PhoneCall,
  Brain,
  MessageCircle,
  ShieldCheck,
  Target,
  TrendingUp,
  Check,
} from "lucide-react";
import SEO from "./SEO";

/**
 * Rookie Hunter — one-page A4 B2B entry-level curriculum flyer.
 */
export default function RookieHunterCurriculum() {
  return (
    <main
      data-testid="rookie-hunter-page"
      className="min-h-screen bg-sebyx-bg text-sebyx-text font-body"
    >
      <SEO
        title="Rookie Hunter Bootcamp | B2B Enterprise Lead Generation"
        description="Build a predictable pipeline in 90 days. 12-hour intensive sprint for SDRs, BDRs, and founders. Cold outreach scripts, discovery calls, and objection handling."
        path="/rookie-hunter"
      />
      <header className="print:hidden sticky top-0 z-50 bg-sebyx-bg/95 backdrop-blur border-b border-sebyx-border">
        <div className="max-w-5xl mx-auto px-6 md:px-10 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3 group">
            <ArrowLeft
              size={16}
              className="text-sebyx-accent group-hover:-translate-x-0.5 transition-transform"
            />
            <span className="block w-2 h-2 bg-sebyx-accent" />
            <span className="font-display text-xl font-semibold tracking-tight">
              Sebyx
            </span>
          </Link>
          <div className="flex items-center gap-3">
            <span className="hidden md:inline font-mono text-[10px] tracking-[0.25em] uppercase text-sebyx-muted">
              Rookie Hunter · Curriculum Flyer
            </span>
            <button
              type="button"
              onClick={() => window.print()}
              className="px-5 py-2 bg-sebyx-accent text-sebyx-bg font-semibold text-sm hover:bg-[#3FA097] transition-colors"
            >
              Download PDF
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-5xl mx-auto px-6 md:px-10 py-10 print:p-0 print:max-w-none">
        <RookieHunterFlyer />
      </div>
    </main>
  );
}

const MODULES = [
  {
    n: "M1",
    Icon: Compass,
    title: "The Sales Career Mindset",
    lines: ["Sales as a real career", "5-year income math", "Amateur vs. professional"],
  },
  {
    n: "M2",
    Icon: Sparkles,
    title: "Professional Presence — Digital & Live",
    lines: ["Your LinkedIn as a sales asset", "Voice on a call · body on Zoom", "Building professional equity"],
  },
  {
    n: "M3",
    Icon: PhoneCall,
    title: "Cold Outreach That Gets Replies",
    lines: ["Email openers that don't bounce", "The 15-second phone opener", "LinkedIn DM sequences"],
  },
  {
    n: "M4",
    Icon: Brain,
    title: "B2B Buyer Psychology",
    lines: ["Why companies actually buy", "Mapping the decision unit", "Champion vs. blocker signals"],
  },
  {
    n: "M5",
    Icon: MessageCircle,
    title: "The Discovery Call",
    lines: ["The 5-question framework", "Selling outcomes, not features", "Handoff to demo / next step"],
  },
  {
    n: "M6",
    Icon: ShieldCheck,
    title: "Objection Handling",
    lines: ["\"Send me an email\"", "\"No budget\" · \"Not now\"", "\"We already have a vendor\""],
  },
  {
    n: "M7",
    Icon: Target,
    title: "The Close & Multi-Threading",
    lines: ["Closing your first deal", "Multi-threading a stalled deal", "Ethical urgency (never fake)"],
  },
  {
    n: "M8",
    Icon: TrendingUp,
    title: "CRM, Follow-Up & Referrals",
    lines: ["CRM discipline from Day 1", "The 7-touch follow-up cadence", "Asking for the first referral"],
  },
];

function RookieHunterFlyer() {
  return (
    <section
      data-testid="rookie-hunter-flyer"
      className="relative bg-sebyx-bg border border-sebyx-border sebyx-corner overflow-hidden print:border-0"
      style={{
        width: "210mm",
        height: "297mm",
        margin: "0 auto",
        padding: "12mm 14mm",
      }}
    >
      <div className="absolute inset-0 sebyx-grid-bg opacity-30 pointer-events-none" />
      <div className="relative h-full flex flex-col">
        {/* Brand bar */}
        <div className="flex items-center justify-between pb-3 border-b border-sebyx-border">
          <div className="flex items-center gap-2">
            <span className="block w-2.5 h-2.5 bg-sebyx-accent" />
            <span className="font-display text-xl font-semibold tracking-tight">
              Sebyx
            </span>
            <span className="font-mono text-[8.5px] tracking-[0.3em] uppercase text-sebyx-muted ml-2">
              / Sebyx Academy
            </span>
          </div>
          <span className="font-mono text-[8.5px] tracking-[0.3em] uppercase text-sebyx-muted">
            Course Curriculum · 2026
          </span>
        </div>

        {/* Headline */}
        <div className="mt-4">
          <div className="flex items-center gap-3 mb-2">
            <span className="block w-7 h-px bg-sebyx-accent" />
            <span className="font-mono text-[9px] tracking-[0.3em] uppercase text-sebyx-accent">
              Rookie Hunter · The 2-Day B2B Sales Sprint
            </span>
          </div>
          <h1 className="font-display text-[26px] font-semibold tracking-tighter leading-[1.05] text-sebyx-text">
            Land your{" "}
            <span className="text-sebyx-accent italic font-light">first B2B deal</span>{" "}
            — and the{" "}
            <span className="text-sebyx-accent italic font-light">career</span>{" "}
            that follows.
          </h1>
          <p className="mt-2 text-[11px] leading-snug text-sebyx-muted max-w-4xl">
            An entry-level B2B sales training for ambitious young
            professionals — SDRs, BDRs, inside-sales reps, and new field-sales
            hires across SaaS, industrial, services, and technology. Built by
            18 years of ground-up field expertise.
          </p>
        </div>

        {/* Course facts strip */}
        <div className="mt-3 grid grid-cols-4 gap-px bg-sebyx-border border border-sebyx-border">
          {[
            { k: "2 Days", v: "Weekend · 12 hours" },
            { k: "8 Modules", v: "32 lessons · 60+ scripts" },
            { k: "60% Drills", v: "Live role-play calls" },
            { k: "Certified", v: "Sebyx Rookie Hunter" },
          ].map((s, i) => (
            <div key={i} className="bg-sebyx-bg px-3 py-2 flex flex-col">
              <span className="font-display text-base font-semibold text-sebyx-text leading-tight">
                {s.k}
              </span>
              <span className="font-mono text-[7px] uppercase tracking-[0.25em] text-sebyx-muted">
                {s.v}
              </span>
            </div>
          ))}
        </div>

        {/* Who + What */}
        <div className="mt-3 grid grid-cols-12 gap-3">
          <div className="col-span-7">
            <span className="font-mono text-[9px] tracking-[0.3em] uppercase text-sebyx-accent">
              Who It's For
            </span>
            <ul className="mt-1.5 space-y-1">
              {[
                "Freshers (0-2 years) in B2B — SDR, BDR, inside sales, junior field sales",
                "New hires at SaaS, industrial, services, healthcare, technology firms",
                "Ambitious professionals who want a career in enterprise sales",
                "Companies onboarding a cohort of fresh sales talent (in-house delivery)",
              ].map((s, i) => (
                <li
                  key={i}
                  className="flex items-start gap-2 text-[10.5px] text-sebyx-text/90 leading-snug"
                >
                  <span className="block w-1.5 h-1.5 rotate-45 bg-sebyx-accent mt-1.5 shrink-0" />
                  <span>{s}</span>
                </li>
              ))}
            </ul>
          </div>
          <div className="col-span-5 border-l-2 border-sebyx-accent pl-3 bg-sebyx-surface/40 py-2">
            <span className="font-mono text-[9px] tracking-[0.3em] uppercase text-sebyx-accent">
              What You Take Home
            </span>
            <ul className="mt-1.5 space-y-1 text-[10px] text-sebyx-text/90">
              {[
                "Sebyx Rookie Hunter Certification",
                "40-page printed Playbook (yours to keep)",
                "60+ real scripts — email, phone, LinkedIn",
                "Alumni WhatsApp group access",
                "30-60-90 day pipeline scorecard",
                "Priority for the Sebyx Master Hunter cohort",
              ].map((s) => (
                <li key={s} className="flex items-start gap-2">
                  <Check
                    size={9}
                    strokeWidth={2.5}
                    className="text-sebyx-accent shrink-0 mt-1"
                  />
                  <span>{s}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Curriculum grid */}
        <div className="mt-3">
          <span className="font-mono text-[9px] tracking-[0.3em] uppercase text-sebyx-accent">
            The Curriculum · 8 Modules
          </span>
          <div className="mt-1.5 grid grid-cols-2 gap-1.5">
            {MODULES.map((m) => {
              const Icon = m.Icon;
              return (
                <div
                  key={m.n}
                  className="border border-sebyx-border bg-sebyx-surface/30 p-2 flex flex-col"
                >
                  <div className="flex items-center gap-2 mb-1">
                    <div className="w-6 h-6 border border-sebyx-accent flex items-center justify-center shrink-0">
                      <Icon size={11} className="text-sebyx-accent" />
                    </div>
                    <span className="font-mono text-[8.5px] tracking-[0.3em] uppercase text-sebyx-accent">
                      {m.n}
                    </span>
                    <span className="font-display text-[12px] font-semibold text-sebyx-text leading-tight">
                      {m.title}
                    </span>
                  </div>
                  <ul className="pl-2 space-y-0.5">
                    {m.lines.map((l, i) => (
                      <li
                        key={i}
                        className="flex items-start gap-1.5 text-[9.5px] text-sebyx-text/85 leading-snug"
                      >
                        <span className="block w-1 h-1 rotate-45 bg-sebyx-accent mt-1.5 shrink-0" />
                        {l}
                      </li>
                    ))}
                  </ul>
                </div>
              );
            })}
          </div>
        </div>

        {/* Invitation-only badge — no pricing displayed */}
        <div className="mt-3 border border-sebyx-accent bg-sebyx-surface/40 p-3 flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-2">
            <span className="block w-1.5 h-1.5 rotate-45 bg-sebyx-accent" />
            <span className="font-mono text-[10px] tracking-[0.3em] uppercase text-sebyx-accent">
              Invitation-only Programme
            </span>
            <span className="font-mono text-[9px] tracking-[0.2em] uppercase text-sebyx-muted">
              · 10–12 seats per cohort
            </span>
          </div>
          <div className="font-mono text-[9.5px] tracking-[0.2em] uppercase text-sebyx-muted">
            Investment shared on your qualification call · Online · F2F · 1-on-1 · Corporate
          </div>
        </div>

        {/* CTA + Contact */}
        <div className="mt-auto pt-3">
          <div className="border border-sebyx-accent bg-sebyx-surface/40 p-3 grid grid-cols-12 gap-3 items-center">
            <div className="col-span-7">
              <div className="flex items-center gap-2 mb-1">
                <span className="block w-5 h-px bg-sebyx-accent" />
                <span className="font-mono text-[9px] tracking-[0.3em] uppercase text-sebyx-accent">
                  Enrol / Enquire
                </span>
              </div>
              <h3 className="font-display text-base font-semibold tracking-tight text-sebyx-text leading-tight">
                Reserve your spot for the next Rookie Hunter cohort.
              </h3>
              <p className="text-[10px] text-sebyx-text/90 leading-snug mt-1">
                Seats are intentionally limited. Reply with your name, city,
                and preferred format — Sebyx will share the next available
                cohort dates and enrollment details.
              </p>
            </div>
            <div className="col-span-5 grid grid-cols-1 gap-1.5 text-[10px]">
              <Contact icon={Phone} label="Direct" value="+91 91084 36667" />
              <Contact icon={Mail} label="Email" value="contact@sebyx.com" />
              <Contact icon={Globe} label="Web" value="www.sebyx.com" />
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="pt-2 mt-2 border-t border-sebyx-border flex items-center justify-between text-sebyx-muted font-mono text-[7.5px] tracking-[0.25em] uppercase">
          <span>Sebyx Academy · Rookie Hunter Curriculum</span>
          <span>www.sebyx.com · contact@sebyx.com</span>
        </div>
      </div>
    </section>
  );
}

function Contact({ icon: Icon, label, value }) {
  return (
    <div className="flex items-start gap-1.5">
      <div className="w-5 h-5 border border-sebyx-border bg-sebyx-bg flex items-center justify-center shrink-0">
        <Icon size={9} className="text-sebyx-accent" />
      </div>
      <div className="leading-tight">
        <div className="font-mono text-[6.5px] tracking-[0.25em] uppercase text-sebyx-muted">
          {label}
        </div>
        <div className="font-display text-[10px] text-sebyx-text leading-tight">
          {value}
        </div>
      </div>
    </div>
  );
}
